/**
 * Session storage for preserving OAuth credentials across the choose-organization flow.
 * When a multi-account OAuth user is redirected to the choose-organization page,
 * we need to preserve the OAuth provider and token so they can be included in the
 * subsequent login call after the user picks an organization (BIS-2536).
 */

import { normalizeOAuthProvider } from './oauthUtils';

const STORAGE_KEY = 'ld_oauth_credentials';

type OAuthCredentials = {
  provider: string;
  token: string;
};

/**
 * Returns the stored OAuth credentials, or null if none or unavailable.
 */
export function getOAuthCredentials(): OAuthCredentials | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const credentials = JSON.parse(stored) as OAuthCredentials;
    return {
      ...credentials,
      provider: normalizeOAuthProvider(credentials.provider) || credentials.provider,
    };
  } catch {
    return null;
  }
}

/**
 * Stores OAuth credentials for use after the choose-organization redirect.
 * Call before navigating to the choose-organization page from an OAuth flow.
 * No-op if sessionStorage is unavailable.
 */
export function setOAuthCredentials(credentials: OAuthCredentials): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...credentials,
        provider: normalizeOAuthProvider(credentials.provider) || credentials.provider,
      }),
    );
  } catch {
    // sessionStorage may be unavailable in some contexts (e.g., private browsing)
  }
}

/**
 * Removes the stored OAuth credentials. Call after consuming them on the
 * choose-organization page login call.
 */
export function removeOAuthCredentials(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
}
